import { blynFetch } from './blynFetch';
const BLYN_API_BASE: string =
  (typeof window !== 'undefined' && (window as any).__BLYN_API_URL__)
    || import.meta.env.VITE_BLYN_API_URL
    || 'https://blyn.staging-api.boostt.ai/blyn-v2';

export function getBlynBackendBase(): string {
  if (typeof window !== 'undefined') {
    // 1. ?backend=… query param (dev override)
    const q = new URL(window.location.href).searchParams.get('backend');
    if (q) return q.replace(/\/$/, '');
    // 2. window.__BLYN_BACKEND__
    if ((window as any).__BLYN_BACKEND__) return (window as any).__BLYN_BACKEND__.replace(/\/$/, '');
  }
  return BLYN_API_BASE.replace(/\/$/, '');
}

export interface Identity {
  tenant_id: string;
  user_id?: string;
  project_id?: string | null;
  trace_id?: string;
}

// ==========================================
// Chat SSE Types
// ==========================================
export interface ChatRequest extends Identity {
  user_message: string;
  history?: Array<{ role: 'user' | 'assistant'; content: string }>;
}

export type SseEventName =
  | 'heartbeat' | 'reasoning' | 'text' | 'tool_call'
  | 'suggestions' | 'verifier' | 'done' | 'error';

export interface HeartbeatEvent { trace_id: string; }
export interface ReasoningEvent { chunk: string; }
export interface TextEvent { chunk: string; }

export interface ToolCallStart { phase: 'start'; call_id: string; name: string; }
export interface ToolCallProgress { phase: 'progress'; call_id: string; message: string; }
export interface ToolCallDone {
  phase: 'done';
  call_id: string;
  name: string;
  duration: number;
  outcome: SkillOutcome;
  resultSummary: string;
}
export type ToolCallEvent = ToolCallStart | ToolCallProgress | ToolCallDone;

export interface SuggestionsEvent { chips: string[]; }
export interface VerifierEvent {
  call_id: string;
  passed: boolean;
  confidence: number | null;
  issues: string[];
}
export interface DoneEvent {
  id: string;
  role: 'assistant';
  text: string;
  reasoning: string | null;
  toolCalls: Array<{
    call_id: string;
    name: string;
    duration: number;
    outcome: SkillOutcome;
    resultSummary: string;
  }>;
  suggestions: string[];
  ts: string;
  inputRequired?: {
    tool: string;
    fields: string[];
    message: string;
  };
}
export interface ErrorEvent { kind: SkillErrorKind; message: string; }

export type SkillOutcome =
  | 'success' | 'input_required' | 'verifier_reject'
  | 'hard_blocker' | 'transient' | 'quota'
  | 'auth' | 'safety' | 'unknown';

export type SkillErrorKind = SkillOutcome | 'cancelled';

// ==========================================
// Autonomy Types
// ==========================================
export type AutonomyLevel = 'L0' | 'L1' | 'L2' | 'L3' | 'L4';
export type AutonomyChannel =
  | 'email' | 'sms' | 'direct_mail' | 'digital_ad'
  | 'web' | 'push' | 'slack';

export interface AutonomyState {
  channels: Record<AutonomyChannel, AutonomyLevel>;
  updated_at: string | null;
  unknown_channels: string[];
}

// ==========================================
// Notification Types
// ==========================================
export type NotificationKind =
  | 'BILLING' | 'CAMPAIGN' | 'COMPLIANCE'
  | 'INTEGRATION' | 'SYSTEM' | 'TEAM';

export type NotificationChannel = 'IN_APP' | 'EMAIL' | 'SLACK' | 'SMS';

export interface QuietHours { start: string; end: string; tz: string; }

export interface NotificationPreference {
  preference_id: string;
  tenant_id: string;
  user_id: string;
  kind: NotificationKind;
  channels: NotificationChannel[];
  quiet_hours: QuietHours | null;
  version: number;
}

export interface TestDispatchSummary {
  user_id: string;
  deferred: boolean;
  outcomes: Array<{
    channel: string;
    delivered: boolean;
    latency_ms: number;
    error: string | null;
  }>;
}

// ==========================================
// Trust Score Types
// ==========================================
export interface TrustScorePayload {
  score: number | null;
  sample_size: number;
  buckets: Array<{
    lower: number;
    upper: number;
    sample_size: number;
    accuracy: number;
  }>;
  category: string | null;
}

// ==========================================
// Variable Registry Types
// ==========================================
export type VariableState =
  | 'LIVE' | 'PROPOSED' | 'MAPPING' | 'SYNCING' | 'DEPRECATED';

export type VariableCategory =
  | 'IDENTITY' | 'PRODUCT' | 'OFFER' | 'COMPUTED'
  | 'BEHAVIORAL' | 'AGENT' | 'TEMPORAL' | 'CTA' | 'CUSTOM';

export type VariableType =
  | 'STRING' | 'NUMBER' | 'CURRENCY' | 'DATE'
  | 'EMAIL' | 'URL' | 'BOOLEAN';

export interface Variable {
  key: string;
  label: string;
  category: VariableCategory;
  type: VariableType;
  state: VariableState;
  source: string | null;
  sample: unknown;
  description: string;
  fallback: string;
  connectorId: string | null;
  proposedBy: string | null;
  intendedUse: string | null;
  tenantId: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export interface GraphQLSchemaResponse {
  sdl: string;
  operations: string[];
}

// ==========================================
// Project Workspace Types
// ==========================================
export interface ActivityRow {
  [key: string]: unknown;
}

export interface WorkspaceOverview {
  project: {
    id: string;
    name: string;
    tenant_id: string;
    created_at: string;
    updated_at: string;
    archived: boolean;
  };
  member_count: number;
  conversation_count: number;
  audience_count: number;
  journey_count: number;
  staged_action_count: number;
  creative_count: number;
  kb_source_count: number;
  recent_activity: ActivityRow[];
  permissions: {
    can_edit: boolean;
    can_invite: boolean;
    can_archive: boolean;
  };
}

// ==========================================
// Starters Types
// ==========================================
export interface RankedStarter {
  id: string;
  title: string;
  category: string;
  description: string;
  icon: string;
  route_hint: string | null;
  prompt: string;
  caret_position: number;
  routes_to: string[];
  tags: string[];
}

export interface StartersResponse {
  starters: RankedStarter[];
  count: number;
}

// ==========================================
// Admin / Blyn Mode Types
// ==========================================
export interface BlynModeResponse {
  mode: 'disabled' | 'shadow' | 'enabled';
  valid_modes: string[];
  process_started_at: string;
  description: string;
}

// ==========================================
// Conversations
// ==========================================
export interface ConversationCreateRequest extends Identity {
  title?: string;
}

export interface ConversationSummary {
  conversation_id: string;
  title: string;
  updated_at: string | null;
  turns: number;
  preview: string;
  project_id: string | null;
}

export async function createConversation(req: ConversationCreateRequest): Promise<{ conversation_id: string }> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/conversations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req)
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function listConversations(tenant_id: string, project_id?: string, limit = 50, cursor?: string): Promise<{ items: ConversationSummary[], count: number, next_cursor: string | null }> {
  const base = getBlynBackendBase();
  const params = new URLSearchParams({ tenant_id, limit: String(limit) });
  if (project_id) params.append('project_id', project_id);
  if (cursor) params.append('cursor', cursor);
  
  const res = await blynFetch(`${base}/api/v1/conversations?${params.toString()}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function getConversationState(conversation_id: string, tenant_id: string): Promise<any> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/conversations/${conversation_id}?tenant_id=${tenant_id}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function duplicateConversation(conversation_id: string, tenant_id: string): Promise<ConversationSummary> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/conversations/${conversation_id}/duplicate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenant_id }),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function archiveConversation(conversation_id: string, tenant_id: string): Promise<{ archived: boolean }> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/conversations/${conversation_id}?tenant_id=${tenant_id}`, {
    method: 'DELETE'
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function deleteConversation(conversation_id: string, tenant_id: string): Promise<{ deleted: boolean }> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/conversations/${conversation_id}?tenant_id=${tenant_id}`, {
    method: 'DELETE',
    headers: { accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export interface ConversationPatchRequest {
  tenant_id: string;
  title?: string;
  project_id?: string | null;
}

export async function patchConversation(
  conversation_id: string,
  body: ConversationPatchRequest
): Promise<ConversationSummary> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/conversations/${conversation_id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// BlynEvent
// ==========================================
export type BlynEventType =
  | 'blyn.decision.made' | 'blyn.checkpoint.resolved'
  | 'blyn.creative.approved' | 'blyn.creative.rejected'
  | 'blyn.template.updated' | 'blyn.product.updated'
  | 'blyn.audience.created' | 'blyn.audience.updated'
  | 'blyn.navigate' | 'blyn.zone.config.updated'
  | 'blyn.variant.status.changed';

export interface BlynEvent {
  type: BlynEventType;
  payload: Record<string, unknown>;
  source: string;
  timestamp: string;
  tenant_id: string;
  project_id?: string;
  user_id?: string;
  event_id?: string;
  schema_version?: number;
}

export interface BlynEventResponse {
  status: 'accepted' | 'rejected';
  reason: string;
  audit_row_hash: string;
  cascade: string[];
}

export async function mirrorBlynEvent(event: BlynEvent): Promise<void> {
  const base = getBlynBackendBase();
  blynFetch(`${base}/api/v1/events/blyn`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(event),
    keepalive: true,
  }).catch(err => console.warn('mirror failed', err));
}

// ==========================================
// Projects
// ==========================================
export async function getProjectWorkspace(project_id: string, tenant_id: string, tenant_role?: string, project_role?: string): Promise<WorkspaceOverview> {
  const base = getBlynBackendBase();
  const params = new URLSearchParams({ tenant_id });
  if (tenant_role) params.append('tenant_role', tenant_role);
  if (project_role) params.append('project_role', project_role);

  const res = await blynFetch(`${base}/api/v1/projects/${project_id}?${params.toString()}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function getProjectTab(project_id: string, tab: string, tenant_id: string, cursor?: string, limit = 50): Promise<any> {
  const base = getBlynBackendBase();
  const params = new URLSearchParams({ tenant_id, limit: String(limit) });
  if (cursor) params.append('cursor', cursor);
  
  const res = await blynFetch(`${base}/api/v1/projects/${project_id}/${tab}?${params.toString()}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Autonomy
// ==========================================
export async function getAutonomyState(tenant_id: string): Promise<AutonomyState> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/autonomy?tenant_id=${tenant_id}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Template Variables
// ==========================================
export async function getTemplateVariables(category?: string): Promise<any> {
  const base = getBlynBackendBase();
  const params = new URLSearchParams();
  if (category) params.append('category', category);
  
  const qs = params.toString();
  const url = qs ? `${base}/api/v1/template-variables?${qs}` : `${base}/api/v1/template-variables`;
  const res = await blynFetch(url);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Starters
// ==========================================
export async function getStarters(options: {
  surface?: string;
  category?: string;
  tenant_id?: string;
  user_id?: string;
  project_id?: string;
  project_name?: string;
  audience_id?: string;
  audience_name?: string;
  session_id?: string;
  limit?: number;
} = {}): Promise<StartersResponse> {
  const base = getBlynBackendBase();
  const params = new URLSearchParams();
  Object.entries(options).forEach(([k, v]) => {
    if (v !== undefined) params.append(k, String(v));
  });
  
  const res = await blynFetch(`${base}/api/v1/starters?${params.toString()}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function recordStarterClickTelemetry(starter_id: string, tenant_id: string, user_id: string, surface: string): Promise<void> {
  const base = getBlynBackendBase();
  blynFetch(`${base}/api/v1/starters/click`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ starter_id, tenant_id, user_id, surface }),
    keepalive: true,
  }).catch(err => console.warn('telemetry failed', err));
}

// ==========================================
// Deep Links
// ==========================================
export async function resolveDeepLink(entity_type: string, entity_id: string, tenant_id: string, owner_tenant_id?: string, params?: Record<string, string>): Promise<{ url: string }> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/deep-links/resolve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ entity_type, entity_id, tenant_id, owner_tenant_id, params })
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Notifications (in-app)
// ==========================================
export interface NotificationItem {
  id: string;
  tenant_id: string;
  user_id?: string;
  kind: NotificationKind;
  category?: string;
  title: string;
  body?: string;
  read: boolean;
  created_at: string;
  metadata?: Record<string, unknown>;
}

export async function listNotifications(tenant_id: string, category?: string, unread_only?: boolean): Promise<{ items: NotificationItem[]; count: number }> {
  const base = getBlynBackendBase();
  const params = new URLSearchParams({ tenant_id });
  if (category) params.append('category', category);
  if (unread_only) params.append('unread_only', 'true');
  const res = await blynFetch(`${base}/api/v1/notifications?${params.toString()}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function markNotificationRead(notification_id: string): Promise<{ updated: boolean }> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/notifications/${encodeURIComponent(notification_id)}/read`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function markAllNotificationsRead(tenant_id: string, user_id?: string): Promise<{ updated: number }> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/notifications/read-all`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenant_id, user_id }),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Notification Preferences
// ==========================================
export async function listNotificationPreferences(tenant_id: string, user_id?: string): Promise<{ items: NotificationPreference[] }> {
  const base = getBlynBackendBase();
  const params = new URLSearchParams({ tenant_id });
  if (user_id) params.append('user_id', user_id);

  const res = await blynFetch(`${base}/api/v1/notification-preferences?${params.toString()}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function getNotificationPreference(kind: NotificationKind, tenant_id: string, user_id: string): Promise<NotificationPreference> {
  const base = getBlynBackendBase();
  const params = new URLSearchParams({ tenant_id, user_id });
  const res = await blynFetch(`${base}/api/v1/notification-preferences/${encodeURIComponent(kind)}?${params.toString()}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export interface NotificationPreferenceInput {
  tenant_id: string;
  user_id: string;
  kind: NotificationKind;
  channels: NotificationChannel[];
  quiet_hours?: QuietHours;
}

export async function createNotificationPreference(preference: NotificationPreferenceInput): Promise<NotificationPreference> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/notification-preferences`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(preference),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function updateNotificationPreference(preference: Partial<NotificationPreferenceInput> & { tenant_id: string }): Promise<NotificationPreference> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/notification-preferences`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(preference),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function deleteNotificationPreference(preference_id: string, tenant_id: string): Promise<{ deleted: boolean }> {
  const base = getBlynBackendBase();
  const res = await blynFetch(
    `${base}/api/v1/notification-preferences/${encodeURIComponent(preference_id)}?tenant_id=${encodeURIComponent(tenant_id)}`,
    { method: 'DELETE' }
  );
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function testNotification(tenant_id: string, user_id: string, kind: NotificationKind): Promise<TestDispatchSummary> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/notifications/test`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenant_id, user_id, kind }),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export function getSlackInstallUrl(tenant_id: string, user_id: string): string {
  const base = getBlynBackendBase();
  const params = new URLSearchParams({ tenant_id, user_id });
  return `${base}/api/v1/notifications/slack/install?${params.toString()}`;
}

// ==========================================
// GraphQL
// ==========================================
export async function getGraphQLSchema(): Promise<GraphQLSchemaResponse> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/graphql/schema`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function executeGraphQL<T>(operation: string, variables: Record<string, any>): Promise<T> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/graphql`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ operation, variables }),
  });
  const body = await res.json();
  if (body.errors) throw new Error(body.errors[0].message);
  return body.data[operation] as T;
}

// ==========================================
// Trust Score
// ==========================================
export async function getTrustScore(tenant_id: string, category?: string): Promise<TrustScorePayload> {
  const base = getBlynBackendBase();
  const params = new URLSearchParams({ tenant_id });
  if (category) params.append('category', category);

  const res = await blynFetch(`${base}/api/v1/trust-score?${params.toString()}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Admin / Health
// ==========================================
export async function getHealth(): Promise<{ status: string; [key: string]: unknown }> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/health`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function getBlynMode(): Promise<BlynModeResponse> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/admin/blyn-mode`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function getRegistryStatus(): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/admin/registry-status`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function reloadRegistry(): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/admin/reload`, { method: 'POST' });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Projects (list / create / patch / delete)
// ==========================================
export interface ProjectSummary {
  id: string;
  name: string;
  tenant_id: string;
  status: string;
  created_at: string;
  updated_at: string;
  archived: boolean;
}

export async function listProjects(tenant_id: string, status?: string, cursor?: string, limit = 50): Promise<{ items: ProjectSummary[]; count: number; next_cursor: string | null }> {
  const base = getBlynBackendBase();
  const params = new URLSearchParams({ tenant_id, limit: String(limit) });
  if (status) params.append('status', status);
  if (cursor) params.append('cursor', cursor);
  const res = await blynFetch(`${base}/api/v1/projects?${params.toString()}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function createProject(body: Record<string, unknown>): Promise<ProjectSummary> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/projects`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function patchProject(project_id: string, tenant_id: string, body: Record<string, unknown>): Promise<ProjectSummary> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/projects/${project_id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...body, tenant_id }),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function archiveProject(project_id: string, tenant_id: string): Promise<{ archived: boolean }> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/projects/${project_id}?tenant_id=${encodeURIComponent(tenant_id)}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function addProjectMember(project_id: string, tenant_id: string, name: string, initials: string): Promise<ProjectSummary> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/projects/${project_id}/members`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenant_id, name, initials }),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function removeProjectMember(project_id: string, tenant_id: string, initials: string): Promise<{ removed: boolean }> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/projects/${project_id}/members/${encodeURIComponent(initials)}?tenant_id=${encodeURIComponent(tenant_id)}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export interface MemberCandidate {
  user_id: number;
  name: string;
  email: string;
  initials: string;
  account_id: number;
  account_name: string;
  sub_account_id: number | null;
  sub_account_name: string | null;
  status: string;
}

export async function getMemberCandidates(account_id: string, include_sub_accounts = true): Promise<{ items: MemberCandidate[]; count: number; search: string | null }> {
  const base = getBlynBackendBase();
  const params = new URLSearchParams({ include_sub_accounts: String(include_sub_accounts) });
  const res = await blynFetch(`${base}/api/v1/accounts/${account_id}/member-candidates?${params.toString()}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Tasks
// ==========================================
export interface TaskSummary {
  task_id: string;
  project_id: string | null;
  tenant_id: string;
  status: string;
  created_at: string;
  updated_at: string;
  [key: string]: unknown;
}

export async function listTasks(tenant_id: string, project_id?: string, status?: string, cursor?: string, limit = 50): Promise<{ items: TaskSummary[]; count: number; next_cursor: string | null }> {
  const base = getBlynBackendBase();
  const params = new URLSearchParams({ tenant_id, limit: String(limit) });
  if (project_id) params.append('project_id', project_id);
  if (status) params.append('status', status);
  if (cursor) params.append('cursor', cursor);
  const res = await blynFetch(`${base}/api/v1/tasks?${params.toString()}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function decideTaskCheckpoint(task_id: string, checkpoint_id: string, body: Record<string, unknown>): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/tasks/${task_id}/checkpoints/${checkpoint_id}/decide`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function pauseTask(task_id: string, body: Record<string, unknown>): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/tasks/${task_id}/pause`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function resumeTask(task_id: string, body: Record<string, unknown>): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/tasks/${task_id}/resume`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function cancelTask(task_id: string, body: Record<string, unknown>): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/tasks/${task_id}/cancel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Staged Actions
// ==========================================
export interface StagedActionSummary {
  action_id: string;
  project_id: string | null;
  tenant_id: string;
  status: string;
  [key: string]: unknown;
}

export async function listStagedActions(tenant_id: string, project_id?: string, status = 'PENDING', cursor?: string, limit = 50): Promise<{ items: StagedActionSummary[]; count: number; next_cursor: string | null }> {
  const base = getBlynBackendBase();
  const params = new URLSearchParams({ tenant_id, status, limit: String(limit) });
  if (project_id) params.append('project_id', project_id);
  if (cursor) params.append('cursor', cursor);
  const res = await blynFetch(`${base}/api/v1/staged-actions?${params.toString()}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function approveStagedAction(action_id: string, body: Record<string, unknown>): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/staged-actions/${action_id}/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function updateStagedAction(action_id: string, body: Record<string, unknown>): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/staged-actions/${action_id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function dismissStagedAction(action_id: string, body: Record<string, unknown>): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/staged-actions/${action_id}/dismiss`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Approvals
// ==========================================
export interface ApprovalSummary {
  approval_id: string;
  tenant_id: string;
  type: string;
  status: string;
  assigned_to: string | null;
  [key: string]: unknown;
}

export async function listApprovals(tenant_id: string, assigned_to?: string, type?: string, status?: string, cursor?: string, limit = 50): Promise<{ items: ApprovalSummary[]; count: number; next_cursor: string | null }> {
  const base = getBlynBackendBase();
  const params = new URLSearchParams({ tenant_id, limit: String(limit) });
  if (assigned_to) params.append('assigned_to', assigned_to);
  if (type) params.append('type', type);
  if (status) params.append('status', status);
  if (cursor) params.append('cursor', cursor);
  const res = await blynFetch(`${base}/api/v1/approvals?${params.toString()}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function approveApproval(approval_id: string, body: Record<string, unknown>): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/approvals/${approval_id}/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function rejectApproval(approval_id: string, body: Record<string, unknown>): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/approvals/${approval_id}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function discussApproval(approval_id: string, body: Record<string, unknown>): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/approvals/${approval_id}/discuss`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Nudges
// ==========================================
export interface NudgeSummary {
  nudge_id: string;
  tenant_id: string;
  user_id: string | null;
  category: string | null;
  severity: string | null;
  dismissed: boolean;
  [key: string]: unknown;
}

export async function listNudges(tenant_id: string, user_id?: string, category?: string, severity?: string, dismissed = false, limit = 50): Promise<{ items: NudgeSummary[]; count: number }> {
  const base = getBlynBackendBase();
  const params = new URLSearchParams({ tenant_id, dismissed: String(dismissed), limit: String(limit) });
  if (user_id) params.append('user_id', user_id);
  if (category) params.append('category', category);
  if (severity) params.append('severity', severity);
  const res = await blynFetch(`${base}/api/v1/nudges?${params.toString()}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function dismissNudge(nudge_id: string, body: Record<string, unknown>): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/nudges/${nudge_id}/dismiss`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Knowledge Sources
// ==========================================
export interface KnowledgeSourceSummary {
  id: string;
  kb_id: string; // alias for id — set by the client after fetch
  tenant_id: string;
  project_id: string | null;
  type: string;
  scope: string | null;
  index_status: string;
  [key: string]: unknown;
}

export async function listKnowledgeSources(tenant_id: string, project_id?: string, scope?: string, type?: string, index_status?: string, cursor?: string, limit = 50): Promise<{ items: KnowledgeSourceSummary[]; count: number; next_cursor: string | null }> {
  const base = getBlynBackendBase();
  const params = new URLSearchParams({ tenant_id, limit: String(limit) });
  if (project_id) params.append('project_id', project_id);
  if (scope) params.append('scope', scope);
  if (type) params.append('type', type);
  if (index_status) params.append('index_status', index_status);
  if (cursor) params.append('cursor', cursor);
  const res = await blynFetch(`${base}/api/v1/knowledge-sources?${params.toString()}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  const data = await res.json();
  data.items = data.items.map((item: KnowledgeSourceSummary) => ({ ...item, kb_id: item.id ?? item.kb_id }));
  return data;
}

// blynFetch has no default timeout — a plain fetch() with no `signal` waits
// indefinitely (browsers impose none themselves). A slow/stalled connection
// to the backend (cold start, large file, network blip) left the upload
// button showing "Uploading…" forever with no error and no way to cancel or
// retry short of a page reload — the reported "taking too long" behavior.
// Scaled by file size so a big brand-voice doc isn't cut off prematurely.
function _uploadTimeoutMs(fileSizeBytes: number): number {
  const BASE_MS = 30_000;
  const PER_MB_MS = 2_000;
  return BASE_MS + Math.ceil(fileSizeBytes / (1024 * 1024)) * PER_MB_MS;
}

export async function uploadKnowledgeSource(tenant_id: string, file: File, project_id?: string, user_id?: string, description?: string): Promise<KnowledgeSourceSummary> {
  const base = getBlynBackendBase();
  const form = new FormData();
  form.append('tenant_id', tenant_id);
  form.append('file', file);
  if (project_id) form.append('project_id', project_id);
  if (user_id) form.append('user_id', user_id);
  if (description) form.append('description', description);
  const controller = new AbortController();
  const timeoutMs = _uploadTimeoutMs(file.size);
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  let res: Response;
  try {
    res = await blynFetch(`${base}/api/v1/knowledge-sources`, {
      method: 'POST', body: form, signal: controller.signal,
    });
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      throw new Error(`Upload timed out after ${Math.round(timeoutMs / 1000)}s — check your connection and try again, or use a smaller file.`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export interface PredictionAudienceFileUpload {
  file_key: string;
  file_name: string;
  size: number;
  columns: string[];
}

/** Pre-upload a prediction audience CSV. The returned `file_key` is what the
 * agent references as an `audience_sources` item of `{type: "s3_key"}` —
 * file bytes must never ride inside a chat turn's JSON body. */
export async function uploadPredictionAudienceFile(file: File): Promise<PredictionAudienceFileUpload> {
  const base = getBlynBackendBase();
  const form = new FormData();
  form.append('file', file);
  const res = await blynFetch(`${base}/api/v1/predictions/audience-file`, { method: 'POST', body: form });
  if (!res.ok) {
    let detail = '';
    try {
      const err = await res.json();
      detail = err?.detail || err?.error || '';
    } catch { /* non-JSON error body */ }
    throw new Error(`Upload failed (${res.status})${detail ? `: ${detail}` : ''}`);
  }
  return res.json();
}

export async function addKnowledgeSourceUrl(body: Record<string, unknown>): Promise<KnowledgeSourceSummary> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/knowledge-sources/url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function deleteKnowledgeSource(kb_id: string, tenant_id: string): Promise<{ deleted: boolean }> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/knowledge-sources/${kb_id}?tenant_id=${encodeURIComponent(tenant_id)}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function deleteJourney(journey_id: string, tenant_id: string): Promise<{ deleted: boolean }> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/journeys/${journey_id}?tenant_id=${encodeURIComponent(tenant_id)}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function downloadKnowledgeSource(kb_id: string, tenant_id: string): Promise<Blob> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/knowledge-sources/${kb_id}/download?tenant_id=${encodeURIComponent(tenant_id)}`);
  if (!res.ok) {
    let msg = `API Error: ${res.status}`;
    try {
      const errBody = await res.json();
      if (errBody && errBody.detail) {
        msg = errBody.detail;
      }
    } catch {}
    throw new Error(msg);
  }
  return res.blob();
}

// ==========================================
// Creatives
// ==========================================
export interface CreativeSummary {
  creative_id: string;
  tenant_id: string;
  project_id: string | null;
  channel: string | null;
  status: string;
  [key: string]: unknown;
}

export async function listCreatives(tenant_id: string, project_id?: string, channel?: string, status?: string, cursor?: string, limit = 50): Promise<{ items: CreativeSummary[]; count: number; next_cursor: string | null }> {
  const base = getBlynBackendBase();
  const params = new URLSearchParams({ tenant_id, limit: String(limit) });
  if (project_id) params.append('project_id', project_id);
  if (channel) params.append('channel', channel);
  if (status) params.append('status', status);
  if (cursor) params.append('cursor', cursor);
  const res = await blynFetch(`${base}/api/v1/creatives?${params.toString()}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function createCreative(body: Record<string, unknown>): Promise<CreativeSummary> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/creatives`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function patchCreative(creative_id: string, body: Record<string, unknown>): Promise<CreativeSummary> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/creatives/${creative_id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function addCreativeVariant(creative_id: string, body: Record<string, unknown>): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/creatives/${creative_id}/variants`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function approveCreativeVariant(creative_id: string, variant_id: string, body: Record<string, unknown>): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/creatives/${creative_id}/variants/${variant_id}/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Products
// ==========================================
export interface ProductSummary {
  product_id: string;
  tenant_id: string;
  status: string | null;
  category: string | null;
  [key: string]: unknown;
}

export async function listProducts(tenant_id: string, status?: string, category?: string, q?: string, cursor?: string, limit = 50): Promise<{ items: ProductSummary[]; count: number; next_cursor: string | null }> {
  const base = getBlynBackendBase();
  const params = new URLSearchParams({ tenant_id, limit: String(limit) });
  if (status) params.append('status', status);
  if (category) params.append('category', category);
  if (q) params.append('q', q);
  if (cursor) params.append('cursor', cursor);
  const res = await blynFetch(`${base}/api/v1/products?${params.toString()}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function createProduct(body: Record<string, unknown>): Promise<ProductSummary> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/products`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function updateProduct(product_id: string, body: Record<string, unknown>): Promise<ProductSummary> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/products/${product_id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function archiveProduct(product_id: string, tenant_id: string): Promise<{ archived: boolean }> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/products/${product_id}?tenant_id=${encodeURIComponent(tenant_id)}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Web Pages
// ==========================================
export interface WebPageSummary {
  page_id: string;
  tenant_id: string;
  [key: string]: unknown;
}

export async function listWebPages(tenant_id: string, cursor?: string, limit = 50): Promise<{ items: WebPageSummary[]; count: number; next_cursor: string | null }> {
  const base = getBlynBackendBase();
  const params = new URLSearchParams({ tenant_id, limit: String(limit) });
  if (cursor) params.append('cursor', cursor);
  const res = await blynFetch(`${base}/api/v1/web-pages?${params.toString()}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function createWebPage(body: Record<string, unknown>): Promise<WebPageSummary> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/web-pages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Insights
// ==========================================
export interface InsightSummary {
  [key: string]: unknown;
}

export async function listInsights(tenant_id: string, category?: string, limit = 20): Promise<{ insights: InsightSummary[]; categories: { id: string; label: string; icon: string }[]; count: number }> {
  const base = getBlynBackendBase();
  const params = new URLSearchParams({ tenant_id, limit: String(limit) });
  if (category) params.append('category', category);
  const res = await blynFetch(`${base}/api/v1/insights?${params.toString()}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Audit Log
// ==========================================
export interface AuditLogRow {
  [key: string]: unknown;
}

export async function listAuditLog(tenant_id: string, user_id?: string, action?: string, project_id?: string, date_range = '30d', cursor?: string, limit = 50): Promise<{ items: AuditLogRow[]; count: number; next_cursor: string | null }> {
  const base = getBlynBackendBase();
  const params = new URLSearchParams({ tenant_id, date_range, limit: String(limit) });
  if (user_id) params.append('user_id', user_id);
  if (action) params.append('action', action);
  if (project_id) params.append('project_id', project_id);
  if (cursor) params.append('cursor', cursor);
  const res = await blynFetch(`${base}/api/v1/audit-log?${params.toString()}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Search
// ==========================================
export async function search(body: Record<string, unknown>): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/search`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Process V1 (non-streaming)
// ==========================================
export async function processV1(req: Record<string, unknown>): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/process`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Artifacts
// ==========================================
export interface ArtifactSummary {
  artifact_id?: string;
  user_id: string;
  tenant_id: string | null;
  label: string | null;
  url?: string;
  created_at?: string;
  [key: string]: unknown;
}

export async function uploadArtifact(file: File, user_id: string, tenant_id?: string, label?: string): Promise<ArtifactSummary> {
  const base = getBlynBackendBase();
  const form = new FormData();
  form.append('file', file);
  form.append('user_id', user_id);
  if (tenant_id) form.append('tenant_id', tenant_id);
  if (label) form.append('label', label);
  const res = await blynFetch(`${base}/api/v1/artifacts/upload`, { method: 'POST', body: form });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function listArtifacts(user_id: string, limit = 50): Promise<{ items: ArtifactSummary[]; count: number }> {
  const base = getBlynBackendBase();
  const params = new URLSearchParams({ user_id, limit: String(limit) });
  const res = await blynFetch(`${base}/api/v1/artifacts?${params.toString()}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function uploadArtifactLegacy(file: File, user_id: string, tenant_id?: string, label?: string): Promise<ArtifactSummary> {
  const base = getBlynBackendBase();
  const form = new FormData();
  form.append('file', file);
  form.append('user_id', user_id);
  if (tenant_id) form.append('tenant_id', tenant_id);
  if (label) form.append('label', label);
  const res = await blynFetch(`${base}/prod/api/v1/artifacts/upload`, { method: 'POST', body: form });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function listArtifactsLegacy(user_id: string, limit = 50): Promise<{ items: ArtifactSummary[]; count: number }> {
  const base = getBlynBackendBase();
  const params = new URLSearchParams({ user_id, limit: String(limit) });
  const res = await blynFetch(`${base}/prod/api/v1/artifacts?${params.toString()}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Legacy process (non-streaming)
// ==========================================
export interface LegacyProcessRequest {
  user_message: string;
  conversation_id: string;
  user_id: string;
  tenant_id: string;
  state?: Record<string, unknown>;
  history?: Array<{ role: 'user' | 'assistant'; content: string }>;
  trace_id?: string;
}

export interface LegacyApiResponse {
  status: 'ok' | 'input_required' | 'error';
  message: string;
  tool_used: string | null;
  conversation_stage: string;
  required_inputs: string[];
  data: Record<string, unknown>;
  trace_id: string;
}

export async function processLegacy(req: LegacyProcessRequest): Promise<LegacyApiResponse> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/prod/api/v1/process`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Memory / Facts
// ==========================================
export interface MemoryFact {
  id: string;
  tenant_id: string;
  scope_ref_id: string | null;
  scope: 'PROJECT' | 'TENANT' | 'GLOBAL';
  statement: string;
  confidence: number;
  origin: string;
  evidence_count: number;
  pinned: boolean;
  status: 'ACTIVE' | 'SUPERSEDED' | 'ARCHIVED';
  reverification_pending: boolean;
  last_observed_at: string;
  used_count: number;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  superseded_by: string | null;
  platform_attribution?: string;
}

export interface MemoryPattern {
  pattern_id: string;
  tenant_id: string;
  scope: string;
  statement: string;
  category: string;
  confidence: number;
  underlying_fact_ids: string[];
  pinned: boolean;
  status: 'ACTIVE' | 'ARCHIVED';
  last_observed_at: string;
  used_count: number;
}

export interface MemoryBudget {
  tenant_id: string;
  monthly_cap_cents: number;
  spent_cents: number;
  period_start: string;
  period_end: string;
  paused_at: string | null;
}

export async function listMemoryFacts(tenant_id: string, scope?: string, project_id?: string, status = 'ACTIVE'): Promise<{ facts: MemoryFact[]; count: number }> {
  const base = getBlynBackendBase();
  const params = new URLSearchParams({ tenant_id, status });
  if (scope) params.append('scope', scope);
  if (project_id) params.append('project_id', project_id);
  const res = await blynFetch(`${base}/api/v1/memory/facts?${params.toString()}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function createMemoryFact(body: { tenant_id: string; project_id?: string | null; scope: string; text: string; origin: string; confidence?: number; pinned?: boolean }): Promise<MemoryFact> {
  const base = getBlynBackendBase();
  const { text, project_id, ...rest } = body;
  const res = await blynFetch(`${base}/api/v1/memory/facts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...rest, statement: text, scope_ref_id: project_id ?? null }),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function updateMemoryFact(fact_id: string, tenant_id: string, body: Partial<{ text: string; pinned: boolean; reverification_pending: boolean }>): Promise<MemoryFact> {
  const base = getBlynBackendBase();
  const { text, ...rest } = body;
  const res = await blynFetch(`${base}/api/v1/memory/facts/${fact_id}?tenant_id=${encodeURIComponent(tenant_id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(text !== undefined ? { ...rest, statement: text } : rest),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function archiveMemoryFact(fact_id: string, tenant_id: string): Promise<{ archived: boolean }> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/memory/facts/${fact_id}?tenant_id=${encodeURIComponent(tenant_id)}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function verifyMemoryFact(fact_id: string, body: { tenant_id: string; confirmed: boolean }): Promise<MemoryFact> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/memory/facts/${fact_id}/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function listMemoryPatterns(tenant_id: string, status = 'ACTIVE'): Promise<{ items: MemoryPattern[]; count: number }> {
  const base = getBlynBackendBase();
  const params = new URLSearchParams({ tenant_id, status });
  const res = await blynFetch(`${base}/api/v1/memory/patterns?${params.toString()}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function archiveMemoryPattern(pattern_id: string, tenant_id: string): Promise<{ archived: boolean }> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/memory/patterns/${pattern_id}?tenant_id=${encodeURIComponent(tenant_id)}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function getMemoryBudget(tenant_id: string): Promise<MemoryBudget> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/memory/budget?tenant_id=${encodeURIComponent(tenant_id)}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function updateMemoryBudget(tenant_id: string, monthly_cap_cents: number): Promise<MemoryBudget> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/memory/budget`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenant_id, monthly_cap_cents }),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Billing
// ==========================================
export interface BillingOverview {
  plan_name: string;
  billing_cycle_end: string;
  direct_mail_wallet_cents: number;
  direct_mail_cap_cents: number;
  agent_runs: number;
  agent_runs_limit: number | null;
  compute_cost_cents: number;
  team_seats_used: number;
  team_seats_total: number;
}

export interface PaymentMethod {
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
  billing_interval: string;
}

export interface Invoice {
  invoice_id: string;
  period_label: string;
  amount_cents: number;
  description: string;
  paid_at: string;
  download_url: string | null;
}

export async function getBillingOverview(tenant_id: string): Promise<BillingOverview> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/billing/overview?tenant_id=${encodeURIComponent(tenant_id)}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function getPaymentMethod(tenant_id: string): Promise<PaymentMethod | null> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/billing/payment-method?tenant_id=${encodeURIComponent(tenant_id)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function listInvoices(tenant_id: string, limit = 10): Promise<{ items: Invoice[] }> {
  const base = getBlynBackendBase();
  const params = new URLSearchParams({ tenant_id, limit: String(limit) });
  const res = await blynFetch(`${base}/api/v1/billing/invoices?${params.toString()}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Integrations
// ==========================================
export interface Integration {
  integration_id: string;
  tenant_id: string;
  name: string;
  role: string;
  status: 'connected' | 'warn' | 'error' | 'disconnected';
  meta: string;
}

export async function listIntegrations(tenant_id: string): Promise<{ items: Integration[] }> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/integrations?tenant_id=${encodeURIComponent(tenant_id)}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function reconnectIntegration(integration_id: string, tenant_id: string): Promise<{ reconnect_url: string }> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/integrations/${integration_id}/reconnect`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenant_id }),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function disconnectIntegration(integration_id: string, tenant_id: string): Promise<{ disconnected: boolean }> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/integrations/${integration_id}?tenant_id=${encodeURIComponent(tenant_id)}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Account / User profile
// ==========================================
export interface UserProfile {
  user_id: string;
  tenant_id: string;
  name: string;
  email: string;
  title: string | null;
  avatar_url: string | null;
  role: string;
  joined_at: string;
}

export async function getCurrentUserProfile(user_id: string, tenant_id: string): Promise<UserProfile> {
  const base = getBlynBackendBase();
  const params = new URLSearchParams({ user_id, tenant_id });
  const res = await blynFetch(`${base}/api/v1/account?${params.toString()}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function updateCurrentUserProfile(body: { user_id: string; tenant_id: string; name?: string; title?: string }): Promise<UserProfile> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/account`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function signOutAllSessions(user_id: string, tenant_id: string): Promise<{ signed_out: boolean }> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/account/sign-out-all`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id, tenant_id }),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function deleteAccount(user_id: string, tenant_id: string): Promise<{ deleted: boolean }> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/account?user_id=${encodeURIComponent(user_id)}&tenant_id=${encodeURIComponent(tenant_id)}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Team / Members
// ==========================================
export interface TeamMember {
  member_id: string;
  tenant_id: string;
  user_id: string;
  name: string;
  email: string;
  role: string;
  joined_at: string;
  is_current_user: boolean;
}

export async function listTeamMembers(tenant_id: string): Promise<{ items: TeamMember[]; count: number }> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/team/members?tenant_id=${encodeURIComponent(tenant_id)}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function inviteTeamMember(body: { tenant_id: string; email: string; role: string }): Promise<{ invitation_id: string }> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/team/invitations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function updateTeamMember(member_id: string, body: { tenant_id: string; role?: string }): Promise<TeamMember> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/team/members/${member_id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function removeTeamMember(member_id: string, tenant_id: string): Promise<{ removed: boolean }> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/team/members/${member_id}?tenant_id=${encodeURIComponent(tenant_id)}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}


// ==========================================
// Onboarding
// ==========================================
export interface OnboardingState {
  completed: boolean;
  company_name: string | null;
  vertical: string | null;
  use_case: string | null;
  connected_source: string | null;
  connected_integration_id: string | null;
  brand_voice_kb_id: string | null;
  brand_voice_file_name: string | null;
  step: number | null;
}

export interface OnboardingCompleteRequest {
  company_name: string;
  vertical: string;
  use_case: string;
  connected_source?: string | null;
  connected_integration_id?: string | null;
  brand_voice_kb_id?: string | null;
  brand_voice_file_name?: string | null;
  step?: number | null;
  partial?: boolean;
}

export async function getOnboarding(tenant_id: string): Promise<OnboardingState> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/onboarding?tenant_id=${encodeURIComponent(tenant_id)}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function completeOnboarding(tenant_id: string, body: OnboardingCompleteRequest): Promise<OnboardingState> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/onboarding?tenant_id=${encodeURIComponent(tenant_id)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Glossary
// ==========================================
export interface GlossaryTerm {
  term_id: string;
  term: string;
  definition: string;
  [key: string]: unknown;
}

export async function listGlossary(q?: string): Promise<{ items: GlossaryTerm[]; count: number }> {
  const base = getBlynBackendBase();
  const params = new URLSearchParams();
  if (q) params.append('q', q);
  const qs = params.toString();
  const res = await blynFetch(`${base}/api/v1/glossary${qs ? `?${qs}` : ''}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function getGlossaryTerm(term_id: string): Promise<GlossaryTerm> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/glossary/${encodeURIComponent(term_id)}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Autonomy — decide
// ==========================================
export async function decideAutonomy(action_type: string, autonomy_level: string, context?: Record<string, unknown>): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/autonomy/decide`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action_type, autonomy_level, context: context ?? {} }),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Staged Actions — reject + bulk approve
// ==========================================
export async function rejectStagedAction(action_id: string, body: { tenant_id: string; rejected_by: string; reason: string; category?: string | null; rejected_slots?: string[]; content_diff?: Record<string, unknown> }): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/staged-actions/${action_id}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function getStagedAction(action_id: string, tenant_id: string): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/staged-actions/${action_id}?tenant_id=${encodeURIComponent(tenant_id)}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function executeStagedAction(action_id: string, tenant_id: string): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/staged-actions/${action_id}/execute?tenant_id=${encodeURIComponent(tenant_id)}`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export interface BulkApproveRequest {
  tenant_id: string;
  ids: string[];
  approved_by: string;
  reason?: string | null;
}

export async function bulkApproveStagedActions(body: BulkApproveRequest): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/staged-actions/bulk-approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function bulkApproveApprovals(body: BulkApproveRequest): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/approvals/bulk-approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function bulkApproveCreatives(body: BulkApproveRequest): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/creatives/bulk-approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Individual item getters (missing from original)
// ==========================================
export async function getTask(task_id: string, tenant_id: string): Promise<TaskSummary> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/tasks/${task_id}?tenant_id=${encodeURIComponent(tenant_id)}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function getApproval(approval_id: string, tenant_id: string): Promise<ApprovalSummary> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/approvals/${approval_id}?tenant_id=${encodeURIComponent(tenant_id)}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function getNudge(nudge_id: string, tenant_id: string): Promise<NudgeSummary> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/nudges/${nudge_id}?tenant_id=${encodeURIComponent(tenant_id)}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function getWebPage(page_id: string, tenant_id: string): Promise<WebPageSummary> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/web-pages/${page_id}?tenant_id=${encodeURIComponent(tenant_id)}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function getKnowledgeSource(kb_id: string, tenant_id: string): Promise<KnowledgeSourceSummary> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/knowledge-sources/${kb_id}?tenant_id=${encodeURIComponent(tenant_id)}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  const item = await res.json();
  return { ...item, kb_id: item.id ?? item.kb_id };
}

export async function patchKnowledgeSource(kb_id: string, body: { tenant_id: string; name?: string | null; scope?: string | null; tags?: string[] | null; project_id?: string | null }): Promise<KnowledgeSourceSummary> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/knowledge-sources/${kb_id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function getProduct(product_id: string, tenant_id: string): Promise<ProductSummary> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/products/${product_id}?tenant_id=${encodeURIComponent(tenant_id)}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function getCreative(creative_id: string, tenant_id: string): Promise<CreativeSummary> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/creatives/${creative_id}?tenant_id=${encodeURIComponent(tenant_id)}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Integrations — get single, connect, status
// ==========================================
export async function getIntegration(integration_id: string, tenant_id: string): Promise<Integration> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/integrations/${integration_id}?tenant_id=${encodeURIComponent(tenant_id)}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function connectIntegration(integration_id: string, tenant_id: string): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/integrations/${integration_id}/connect`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenant_id }),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function disconnectIntegrationV2(integration_id: string, tenant_id: string): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/integrations/${integration_id}/disconnect`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenant_id }),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function setIntegrationStatus(integration_id: string, tenant_id: string, status: string): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/integrations/${integration_id}/status`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenant_id, status }),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Memory — additional operations
// ==========================================
export async function archiveMemoryFactV2(fact_id: string, tenant_id: string): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/memory/facts/${fact_id}/archive?tenant_id=${encodeURIComponent(tenant_id)}`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function restoreMemoryFact(fact_id: string, tenant_id: string): Promise<MemoryFact> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/memory/facts/${fact_id}/restore?tenant_id=${encodeURIComponent(tenant_id)}`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function scopeMemoryFact(fact_id: string, body: { tenant_id: string; new_scope: string; new_scope_ref_id?: string | null }): Promise<MemoryFact> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/memory/facts/${fact_id}/scope`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function pinMemoryFact(fact_id: string, tenant_id: string, pinned: boolean): Promise<MemoryFact> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/memory/facts/${fact_id}/pin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenant_id, pinned }),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function confirmMemoryReverification(fact_id: string, tenant_id: string): Promise<MemoryFact> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/memory/facts/${fact_id}/confirm-reverification?tenant_id=${encodeURIComponent(tenant_id)}`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function getMemoryReverificationQueue(tenant_id: string, project_id?: string): Promise<{ items: MemoryFact[]; count: number }> {
  const base = getBlynBackendBase();
  const params = new URLSearchParams({ tenant_id });
  if (project_id) params.append('project_id', project_id);
  const res = await blynFetch(`${base}/api/v1/memory/reverification?${params.toString()}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function getMemoryCounts(tenant_id: string): Promise<{ active: number; pinned: number; reverification_pending: number; [key: string]: unknown }> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/memory/counts?tenant_id=${encodeURIComponent(tenant_id)}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Blyn Audit Log (separate from /api/v1/audit-log)
// ==========================================
export interface BlynAuditLogRow {
  tenant_id: string;
  actor: string;
  action: string;
  target_type: string;
  target_id: string;
  outcome: string;
  project_id: string | null;
  detail: Record<string, unknown>;
  [key: string]: unknown;
}

export async function listBlynAuditLog(params: {
  tenant_id: string;
  since?: string | null;
  until?: string | null;
  actor?: string | null;
  action?: string | null;
  target_type?: string | null;
  target_id?: string | null;
  project_id?: string | null;
  limit?: number;
}): Promise<{ items: BlynAuditLogRow[]; count: number }> {
  const base = getBlynBackendBase();
  const qs = new URLSearchParams({ tenant_id: params.tenant_id, limit: String(params.limit ?? 100) });
  if (params.since) qs.append('since', params.since);
  if (params.until) qs.append('until', params.until);
  if (params.actor) qs.append('actor', params.actor);
  if (params.action) qs.append('action', params.action);
  if (params.target_type) qs.append('target_type', params.target_type);
  if (params.target_id) qs.append('target_id', params.target_id);
  if (params.project_id) qs.append('project_id', params.project_id);
  const res = await blynFetch(`${base}/api/v1/blyn/audit-log?${qs.toString()}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function appendBlynAuditLogEntry(body: { tenant_id: string; actor: string; action: string; target_type: string; target_id: string; outcome?: string; project_id?: string | null; detail?: Record<string, unknown> }): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/blyn/audit-log`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Experiments
// ==========================================
export interface ExperimentSummary {
  experiment_id: string;
  tenant_id: string;
  page_id: string;
  zone_id: string;
  name: string;
  status: string;
  project_id: string | null;
  [key: string]: unknown;
}

export interface CreateExperimentBody {
  tenant_id: string;
  page_id: string;
  zone_id: string;
  name: string;
  arms: Array<Record<string, unknown>>;
  hypothesis?: string | null;
  split_algorithm?: string;
  traffic_cap?: number;
  min_sample_per_arm?: number;
  confidence_threshold?: number;
  project_id?: string | null;
  experiment_id?: string | null;
}

export async function listExperiments(tenant_id: string, options: { project_id?: string; page_id?: string; zone_id?: string; status?: string; limit?: number } = {}): Promise<{ items: ExperimentSummary[]; count: number }> {
  const base = getBlynBackendBase();
  const params = new URLSearchParams({ tenant_id, limit: String(options.limit ?? 50) });
  if (options.project_id) params.append('project_id', options.project_id);
  if (options.page_id) params.append('page_id', options.page_id);
  if (options.zone_id) params.append('zone_id', options.zone_id);
  if (options.status) params.append('status', options.status);
  const res = await blynFetch(`${base}/api/v1/experiments?${params.toString()}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function createExperiment(body: CreateExperimentBody): Promise<ExperimentSummary> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/experiments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function getExperiment(experiment_id: string, tenant_id: string): Promise<ExperimentSummary> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/experiments/${experiment_id}?tenant_id=${encodeURIComponent(tenant_id)}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function launchExperiment(experiment_id: string, tenant_id: string): Promise<ExperimentSummary> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/experiments/${experiment_id}/launch?tenant_id=${encodeURIComponent(tenant_id)}`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function pauseExperiment(experiment_id: string, tenant_id: string, reason?: string | null): Promise<ExperimentSummary> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/experiments/${experiment_id}/pause`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenant_id, reason: reason ?? null }),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function concludeExperiment(experiment_id: string, body: { tenant_id: string; winner_arm_id?: string | null; winner_confidence?: number | null }): Promise<ExperimentSummary> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/experiments/${experiment_id}/conclude`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function getExperimentSignificance(experiment_id: string, tenant_id: string): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/experiments/${experiment_id}/significance?tenant_id=${encodeURIComponent(tenant_id)}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function validateExperimentConfig(body: { tenant_id: string; page_id: string; zone_id: string; arm_ids: string[] }): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/experiments/validate-config`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Web Pages v2 (/api/v1/web/pages — zone & variant management)
// ==========================================
export async function listWebPagesV2(tenant_id: string, project_id?: string): Promise<{ items: WebPageSummary[]; count: number }> {
  const base = getBlynBackendBase();
  const params = new URLSearchParams({ tenant_id });
  if (project_id) params.append('project_id', project_id);
  const res = await blynFetch(`${base}/api/v1/web/pages?${params.toString()}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function createWebPageV2(body: { tenant_id: string; page_name: string; page_url: string; project_id?: string | null; page_type?: string; page_id?: string | null; sdk_config?: Record<string, unknown> }): Promise<WebPageSummary> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/web/pages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function getWebPageV2(page_id: string, tenant_id: string): Promise<WebPageSummary> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/web/pages/${page_id}?tenant_id=${encodeURIComponent(tenant_id)}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function deleteWebPageV2(page_id: string, tenant_id: string): Promise<{ deleted: boolean }> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/web/pages/${page_id}?tenant_id=${encodeURIComponent(tenant_id)}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function addWebPageZone(page_id: string, body: { tenant_id: string; zone_type: string; name: string; selector: string; fallback: Record<string, unknown>; autonomy?: string; experiment_id?: string | null; priority?: number; ttl?: number; tracking?: Record<string, unknown> | null; targeting?: Record<string, unknown> | null; zone_id?: string | null }): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/web/pages/${page_id}/zones`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function deleteWebPageZone(page_id: string, zone_id: string, tenant_id: string): Promise<{ deleted: boolean }> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/web/pages/${page_id}/zones/${zone_id}?tenant_id=${encodeURIComponent(tenant_id)}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// Updates a zone in place (e.g. autonomy level, config) instead of delete + recreate.
// Every create/update/delete under /api/v1/web/pages/... writes an audit log entry.
export async function patchWebPageZoneV2(page_id: string, zone_id: string, body: Record<string, unknown>): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/web/pages/${page_id}/zones/${zone_id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function addWebPageZoneVariant(page_id: string, zone_id: string, body: { tenant_id: string; arm_id: string; name: string; content: Record<string, unknown>; weight?: number; created_by?: string; status?: string }): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/web/pages/${page_id}/zones/${zone_id}/variants`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function setWebPageZoneVariantStatus(page_id: string, zone_id: string, arm_id: string, tenant_id: string, new_status: string): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/web/pages/${page_id}/zones/${zone_id}/variants/${arm_id}/status`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenant_id, new_status }),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function recordWebPageZoneVariantEvent(page_id: string, zone_id: string, arm_id: string, tenant_id: string, event_type: string, delta = 1): Promise<unknown> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/web/pages/${page_id}/zones/${zone_id}/variants/${arm_id}/events`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenant_id, event_type, delta }),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

// ==========================================
// Nudge Preferences
// ==========================================
export interface NudgePreferences {
  tenant_id: string;
  user_id: string;
  categories: string[];
  quiet_hours_from: string;
  quiet_hours_to: string;
}

export async function getNudgePreferences(tenant_id: string, user_id: string): Promise<NudgePreferences> {
  const base = getBlynBackendBase();
  const params = new URLSearchParams({ tenant_id, user_id });
  const res = await blynFetch(`${base}/api/v1/nudges/preferences?${params.toString()}`);
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}

export async function updateNudgePreferences(body: { tenant_id: string; user_id: string; categories?: string[]; quiet_hours_from?: string; quiet_hours_to?: string }): Promise<NudgePreferences> {
  const base = getBlynBackendBase();
  const res = await blynFetch(`${base}/api/v1/nudges/preferences`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API Error: ${res.status}`);
  return res.json();
}
